import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGuest } from '../contexts/GuestContext';
import { useLanguage } from '../contexts/LanguageContext';
import '../styles/guest.css';

function GuestLoginPage() {
    const [searchParams] = useSearchParams();
    const [accessCode, setAccessCode] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useGuest();
    const { t, l } = useLanguage();
    const navigate = useNavigate();

    useEffect(() => {
        // Check for code in URL (can be encoded or plain text)
        const codeParam = searchParams.get('code');
        if (codeParam) {
            try {
                // Try to decode as base64
                const decoded = atob(codeParam);
                setAccessCode(decoded);
                // Auto-login with decoded code
                autoLogin(decoded);
            } catch (e) {
                // If decoding fails, treat as plain text
                setAccessCode(codeParam);
                autoLogin(codeParam);
            }
        }
    }, [searchParams]);

    const autoLogin = async (code) => {
        setLoading(true);
        const result = await login(code);
        
        if (result.success) {
            // Check if we came from RSVP
            const returnTo = searchParams.get('returnTo');
            if (returnTo) {
                navigate(l(returnTo));
                // Scroll to section after navigation
                setTimeout(() => {
                    const element = document.getElementById('rsvp');
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }, 100);
            } else {
                navigate(l('/'));
            }
        } else {
            setError(result.error === 'Invalid access code' ? t('invalidCode') : (result.error || t('invalidCode')));
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const result = await login(accessCode);
        
        if (result.success) {
            // Check if we came from RSVP
            const returnTo = searchParams.get('returnTo');
            if (returnTo) {
                navigate(l(returnTo));
                // Scroll to section after navigation
                setTimeout(() => {
                    const element = document.getElementById('rsvp');
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }, 100);
            } else {
                navigate(l('/'));
            }
        } else {
            setError(result.error === 'Invalid access code' ? t('invalidCode') : (result.error || t('invalidCode')));
        }
        
        setLoading(false);
    };

    return (
        <div className="guest-login-page">
            <div className="guest-login-container">
                <h1>{t('guestLogin')}</h1>
                <p>{t('guestLoginDesc')}</p>
                
                <form onSubmit={handleSubmit} className="guest-login-form">
                    <input
                        type="text"
                        value={accessCode}
                        onChange={(e) => setAccessCode(e.target.value)}
                        placeholder={t('accessCodeLabel')}
                        disabled={loading}
                        autoFocus
                    />
                    
                    {error && <div className="error-message">{error}</div>}
                    
                    <button type="submit" disabled={loading || !accessCode}>
                        {loading ? t('loggingIn') : t('login')}
                    </button>
                </form>
                
                <button 
                    className="back-to-home"
                    onClick={() => navigate(l('/'))}
                >
                    {t('backToHome')}
                </button>
            </div>
        </div>
    );
}

export default GuestLoginPage;
